import React from 'react';

import DownloadIconSrc from 'assets/icons/download.svg';

import i18n from 'i18next';

const EULA_PDF_URL = '/eula.pdf';

type Props = {
  onDone: () => void;
};

export default function EulaContent({onDone}: Props) {
  return (
    <div className="bg-white rounded-gigantic py-6 px-10 flex flex-col z-index-50 relative h-fit">
      <div className="flex">
        <div
          onClick={onDone}
          className="flex-grow text-dodger-blue hover:underline cursor-pointer"
        >
          {'< Terug'}
        </div>
        <a
          className="flex text-dodger-blue hover:underline cursor-pointer relative"
          href={EULA_PDF_URL}
          download
        >
          <img
            src={DownloadIconSrc}
            alt={i18n.t('common.download')}
            width={36}
            height={36}
            className="absolute"
            style={{left: -36, top: -6}}
          />
          {i18n.t('common.download')}
        </a>
      </div>
      <article className="h-fit mt-4">
        <h1 className="text-2xl font-semibold mb-2">
          GEBRUIKSVOORWAARDEN WISBITS
        </h1>
        <p className="my-2">Versie: 1.0</p>
        <p className="my-2">Datum: 25 november 2020</p>

        <p className="my-2">
          Deze Gebruiksvoorwaarden beschrijven de voorwaarden waaronder gebruik
          mag worden gemaakt van de (web)applicatie ’Wisbits’, die Gebruiker als
          (eind)gebruiker van Organisatie kan gebruiken. Organisatie heeft
          middels de Wisbits (web)applicatie leermodules ontwikkeld en
          beschikbaar gesteld. Wisbits biedt Gebruiker de mogelijkheid om,
          afhankelijk van het type account dat Gebruiker heeft verkregen van
          Organisatie, deze leermodules van Organisatie te beheren, ontwikkelen,
          of via mobile learning eenvoudig te kunnen volgen. Door de
          (web)applicatie Wisbits te gebruiken, gaat Gebruiker akkoord met deze
          Gebruiksvoorwaarden.
        </p>
        <h2 className="text-1xl font-semibold my-4">ARTIKEL 1. DEFINITIES</h2>
        <p className="my-2">
          De in de Gebruiksvoorwaarden met hoofdletters geschreven termen hebben
          de hierna volgende betekenis.
        </p>
        <p className="my-2">
          1.1. <strong>App</strong>: de mobiele applicatie ‘Wisbits’, zoals
          geleverd door BlendIN en aan Gebruiker aangeboden door Organisatie via
          de Apple App Store of de Google Play Store.
        </p>
        <p className="my-2">
          1.2. <strong>BlendIN</strong>: het bedrijf blendIn technology B.V.,
          ingeschreven bij de Kamer van Koophandel onder dossiernummer 75195054.
        </p>
        <p className="my-2">
          1.3. <strong>Gebruiker</strong>: de natuurlijke persoon die
          gebruikmaakt van de App of het Platform, waaronder zowel
          eindgebruikers van de App als admin- en ontwikkelaarsgebruikers van
          het Platform worden verstaan.
        </p>
        <p className="my-2">
          1.4. <strong>Gebruiksvoorwaarden</strong>: de onderhavige voorwaarden.
        </p>
        <p className="my-2">
          1.5. <strong>Organisatie</strong>: de organisatie die Gebruiker
          toegang heeft verleend tot de App en waarmee BlendIn een overeenkomst
          heeft tot levering van de App. Organisatie is jegens Gebruiker
          verantwoordelijk voor diens gebruik van de App als (eind)gebruiker.
        </p>
        <p className="my-2">
          1.6. <strong>Platform</strong>: de webapplicatie Wisbits, inclusief
          het admin paneel en de authoring tool waarmee Gebruiker
          administratieve en/of ontwikkelfuncties kan uitoefenen.
        </p>
        <h2 className="text-1xl font-semibold my-4">
          ARTIKEL 2. TOEGANG EN ACCOUNT
        </h2>
        <p className="my-2">
          2.1. BlendIN levert zowel een App als een Platform. Afhankelijk van of
          Gebruiker toegang verkrijgt tot de App met een eindgebruikersaccount
          om leermodules te volgen, of tot het Platform met een admin- of
          ontwikkelaccount om eindgebruikers te beheren of leermodules te
          ontwikkelen, kan de wijze van toewijzing van een account verschillen.
        </p>
        <p className="my-2">
          2.2. Om de App te kunnen gebruiken heeft Gebruiker moet Gebruiker de
          App downloaden via de Apple App Store of de Google Playstore. Tevens
          heeft Gebruiker een account nodig. Organisatie kan een account
          aanmaken voor Gebruiker en middels dit account toegang verstrekken tot
          de App. Gebruiker kan vervolgens zelf een wachtwoord registreren.
          Nadat de registratie is afgerond, kan Gebruiker direct inloggen op het
          account en de App gebruiken.
        </p>
        <p className="my-2">
          2.3. Om het Platform te kunnen gebruiken heeft Gebruiker een admin- of
          ontwikkelaccount nodig. Het Platform is benaderbaar via de website van
          BlendIN via een webportaal op{' '}
          <a href="https://admin.wisbits.nl/" className="text-dodger-blue">
            https://admin.wisbits.nl/
          </a>
          . BlendIN kan op aanvraag van Organisatie een account aanmaken voor
          Gebruiker en middels dit account toegang verstrekken tot het Platform.
          Gebruiker kan vervolgens zelf een wachtwoord registreren. Nadat de
          registratie is afgerond, kan Gebruiker direct inloggen op het account
          en het Platform gebruiken. Admin accounts kunnen onder andere gebruikt
          worden om accounts aan te maken voor eindgebruikers van de App.
        </p>
        <p className="my-2">
          2.4. Gebruiker dient de toegang tot het account middels een
          gebruikersnaam en een wachtwoord af te schermen voor onbevoegden. In
          het bijzonder dient Gebruiker het wachtwoord strikt geheim te houden.
          BlendIN mag ervan uitgaan dat alles wat gebeurt vanaf het account van
          Gebruiker na aanmelding met de bijbehorende gebruikersnaam en
          wachtwoord, onder leiding en toezicht van Gebruiker gebeurt. Gebruiker
          is dus aansprakelijk en verantwoordelijk voor al deze handelingen.
        </p>
        <p className="my-2">
          2.5. In geval van een vermoeden van misbruik van het account, dient
          Gebruiker BlendIN onverwijld op de hoogte te stellen en dient
          Gebruiker de inloggegevens te wijzigen.
        </p>
        <h2 className="text-1xl font-semibold my-4">
          ARTIKEL 3. GEBRUIKSREGELS
        </h2>
        <p className="my-2">
          3.1. Het is Gebruiker niet toegestaan om de App of het Platform te
          gebruiken voor handelingen die in strijd zijn met de Nederlandse of
          andere toepasselijke wet- en regelgeving. Hieronder valt onder meer
          het opslaan of verspreiden van informatie via de App of het Platform
          die smadelijk, lasterlijk of racistisch is, alsook het lastigvallen
          van andere gebruikers van de App of het Platform of het zich voordoen
          als een andere persoon. Daarnaast is het, zelfs niet als het wettelijk
          is toegestaan, niet toegestaan om (content te plaatsen met):
        </p>
        <ul style={{listStyleType: 'disc', listStylePosition: 'inside'}}>
          <li>onfatsoenlijk taalgebruik te gebruiken;</li>
          <li>
            informatie te plaatsen op plaatsen waar dat ongewenst is
            (off-topic);
          </li>
          <li>
            informatie te plaatsen in strijd met enige op de App of het Platform
            nader gestelde inhoudelijke eisen of instructies;
          </li>
          <li>
            informatie te verspreiden in strijd met intellectuele
            eigendomsrechten van derden, in het bijzonder het auteursrecht;
          </li>
          <li>
            de persoonlijke levenssfeer van derden te schenden, bijvoorbeeld
            door zonder toestemming of noodzaak verspreiden van persoonsgegevens
            van derden of het (herhaaldelijk) benaderen van derden met
            ongevraagde communicatie;
          </li>
          <li>
            de App of het Platform voor commerciële doeleinden in te zetten,
            tenzij dit kennelijk is toegestaan.
          </li>
        </ul>
        <p className="my-2">
          3.2. Het is aan Gebruiker tevens niet toegestaan om:
        </p>
        <ul style={{listStyleType: 'disc', listStylePosition: 'inside'}}>
          <li>
            de broncode van de App te reverse engineeren of de App of het
            Platform te decompileren, behoudens voor zover dit bij bepaling van
            dwingend recht of toepasselijke opensourcelicentie is toegestaan;
          </li>
          <li>de App of het Platform in kopie te geven aan derden;</li>
          <li>
            de App zonder uitdrukkelijke toestemming van BlendIN in sublicentie
            te geven of beschikbaar te stellen aan derden;
          </li>
          <li>
            wijzigingen aan te brengen in de App of het Platform, anders dan het
            beoogde gebruik van de App of het Platform of behoudens voor zover
            dit bij bepaling van dwingend recht is toegestaan;
          </li>
          <li>
            aanduidingen van rechthebbenden op de App of het Platform, waaronder
            - maar niet uitsluitend - BlendIN, of delen daarvan te verwijderen
            of onleesbaar te maken.
          </li>
        </ul>
        <p className="my-2">
          3.4. Indien BlendIN constateert dat Gebruiker de in lid 1 of lid 2
          genoemde voorwaarden overtreedt, of een klacht hierover ontvangt, mag
          BlendIN ingrijpen om de overtreding te beëindigen.
        </p>
        <p className="my-2">
          3.5. Indien naar het oordeel van BlendIN hinder, schade of een ander
          gevaar ontstaat voor het functioneren van de computersystemen of het
          netwerk van BlendIN of diens toeleveranciers, is zij gerechtigd alle
          maatregelen te treffen die zij redelijkerwijs nodig acht om dit gevaar
          af te wenden dan wel te voorkomen. Hierbij is BlendIn in het bijzonder
          gerechtigd Gebruikers bijdragen naar eigen inzicht aan te passen of te
          verwijderen.
        </p>
        <p className="my-2">
          3.6. Naast de in deze Gebruiksvoorwaarden opgenomen bepalingen kunnen
          Apples App Store en/of de Google Play Store voorwaarden stellen aan
          het verkrijgen van de App, het gebruiken daarvan en aanverwante
          activiteiten. Raadpleeg hiervoor de gebruiksvoorwaarden en
          privacyverklaring van Apples App Store, de Google Play Store, en de
          eventueel van toepassing zijnde voorwaarden op de website van de
          betreffende aanbieder.
        </p>
        <h2 className="text-1xl font-semibold my-4">
          ARTIKEL 4. INTELLECTUEEL EIGENDOM
        </h2>
        <p className="my-2">
          4.1. Alle rechten op de App, de bijbehorende documentatie en alle
          wijzigingen en uitbreidingen hierop liggen en blijven bij BlendIN
          en/of haar licentiegevers. Gebruiker verkrijgt uitsluitend de
          gebruiksrechten en bevoegdheden die voortvloeien uit de strekking van
          deze Gebruiksvoorwaarden of die schriftelijk worden toegekend en voor
          het overige mag Gebruiker de App niet gebruiken, verveelvoudigen of
          openbaar maken, behalve in gevallen waarin dat wettelijk is
          toegestaan.
        </p>
        <p className="my-2">
          Informatie die Gebruiker publiceert of opslaat via de App is en blijft
          eigendom van Gebruiker (of van zijn licentiegevers). BlendIN verkrijgt
          slechts een gebruiksrecht om de informatie te gebruiken zoals nodig is
          voor het beschikbaar stellen en houden van de App. Dit gebruiksrecht
          wordt voor onbepaalde tijd gegeven en kan niet worden ingetrokken,
          tenzij de wet voortgezet gebruik verbiedt. In de uitzonderlijke
          gevallen waarin sprake is van een zwaarwegend belang tegen voortgezet
          gebruik van de informatie kan BlendIN besluiten tot aanpassing of
          verwijdering.
        </p>
        <p className="my-2">
          4.3. Indien Gebruiker toegang heeft tot het Platform via een
          ontwikkelaccount en zelf (namens Organisatie) content (zoals
          leermodules) ontwikkelt voor eindgebruikers, blijven de intellectuele
          eigendomsrechten op deze content berusten bij Gebruiker, haar
          Organisatie of diens licentiegevers. BlendIN heeft slechts het recht
          om deze content gedurende de looptijd van de overeenkomst met
          Organisatie in te zetten voor leveren van de diensten aan Organisatie,
          conform de overeenkomst met Organisatie.
        </p>
        <h2 className="text-1xl font-semibold my-4">
          ARTIKEL 5. PERSOONSGEGEVENS
        </h2>
        <p className="my-2">
          5.1. Via de App en het Platform verwerkt BlendIN persoonsgegevens.
          Deze persoonsgegevens zullen worden verwerkt conform het privacybeleid
          dat te vinden is onder ‘Privacyverklaring’ in het hoofdmenu van de App
          en via de website van BlendIN ({' '}
          <a href="https://www.wisbits.nl/privacy-policy/">
            https://www.wisbits.nl/privacy-policy/
          </a>
          ).
        </p>
        <h2 className="text-1xl font-semibold my-4">
          ARTIKEL 6. BESCHIKBAARHEID EN ONDERHOUD
        </h2>
        <p className="my-2">
          6.1. BlendIN spant zich in om de App en het Platform te allen tijde
          beschikbaar te laten zijn. De App en het Platform worden echter actief
          onderhouden waardoor onderhoud op elk moment kan plaatsvinden, hetgeen
          kan leiden tot een beperking van de beschikbaarheid. BlendIn spant
          zich in om onderhoud vooraf aan te kondigen.
        </p>
        <p className="my-2">
          6.2. Beschikbare updates voor de App zullen kenbaar worden gemaakt via
          notificatie langs Apples App Store of de Google Play Store, waarbij
          het de verantwoordelijkheid van Gebruiker is deze notificaties bij te
          houden. De updates worden eveneens via voornoemde appstores uitgevoerd
          en vereisen een actieve internetverbinding.
        </p>
        <h2 className="text-1xl font-semibold my-4">
          ARTIKEL 7. DUUR EN OPZEGGING
        </h2>
        <p className="my-2">
          7.1. Deze Gebruiksvoorwaarden treden in werking bij ingebruikname van
          de App of het Platform en blijven dan voor onbepaalde tijd van kracht.
        </p>
        <p className="my-2">
          7.2. Gebruiker kan op ieder moment zijn gebruiksrecht opzeggen door de
          App van zijn mobiele apparaat te verwijderen of te de-installeren, dan
          wel, indien Gebruiker een admin- of ontwikkelaccount heeft, zijn
          account via het gebruikersmenu in het Platform te verwijderen.
        </p>
        <p className="my-2">
          7.3. Na beëindiging van het gebruiksrecht (om welke reden dan ook)
          dient Gebruiker ieder gebruik van App of het Platform te staken en
          gestaakt te houden.
        </p>
        <h2 className="text-1xl font-semibold my-4">
          ARTIKEL 8. WIJZIGINGEN GEBRUIKSVOORWAARDEN
        </h2>
        <p className="my-2">
          8.1. Deze Gebruiksvoorwaarden kunnen door BlendIN worden aangepast.
        </p>
        <p className="my-2">
          8.2. BlendIN zal de wijzigingen of aanvullingen ten minste dertig (30)
          kalenderdagen voor inwerkingtreding aankondigen zodat Gebruiker daar
          kennis van kan nemen.
        </p>
        <p className="my-2">
          8.3. Indien Gebruiker een wijziging of aanvulling niet wenst te
          accepteren, kan Gebruiker tot de datum van inwerkingtreding het
          gebruiksrecht opzeggen door de App te verwijderen of te
          de-installeren, dan wel, indien Gebruiker een admin- of
          ontwikkelaccount heeft, zijn account via het gebruikersmenu in het
          Platform te verwijderen. Gebruik van de App of het Platform na de
          datum van inwerkingtreding geldt als acceptatie van de gewijzigde of
          aangevulde voorwaarden.
        </p>
        <h2 className="text-1xl font-semibold my-4">
          ARTIKEL 9. OVERIGE BEPALINGEN
        </h2>
        <p className="my-2">
          9.1. Op deze Gebruiksvoorwaarden is Nederlands recht van toepassing.
        </p>
        <p className="my-2">
          9.2. Voor zover door de regels van dwingend recht niet anders wordt
          voorgeschreven, zullen alle geschillen tussen partijen in verband met
          de Gebruiksvoorwaarden en/of het gebruik van de App of het Platform
          worden voorgelegd aan de bevoegde Nederlandse rechter voor het
          arrondissement waarin BlendIN gevestigd is.
        </p>
        <p className="my-2">
          9.3. Behalve in gevallen van opzet of bewuste roekeloosheid aan de
          zijde BlendIN is diens aansprakelijkheid als gevolg van een
          toerekenbare tekortkoming, onrechtmatige daad of anderszins,
          uitgesloten. BlendIN heeft een overeenkomst met Organisatie. Indien
          Gebruiker problemen met de App of het Platform of schade ondervindt
          valt dit onder de verantwoordelijkheid van Organisatie en dient
          Gebruiker contact op te nemen met Organisatie.
        </p>
        <p className="my-2">
          9.4. Indien enige bepaling in deze Gebruiksvoorwaarden nietig blijkt
          te zijn, tast dit niet de geldigheid van de gehele Gebruiksvoorwaarden
          aan. Partijen zullen in dat geval ter vervanging (een) nieuwe
          bepaling(en) vaststellen, waarmee zoveel als rechtens mogelijk is aan
          de bedoeling van de oorspronkelijke bepaling gestalte wordt gegeven.
        </p>
        <p className="my-2">
          9.5. BlendIN mag haar rechten en verplichtingen uit deze
          Gebruiksvoorwaarden overdragen aan een derde partij die de betreffende
          bedrijfsactiviteiten overneemt.
        </p>
      </article>
    </div>
  );
}
